/**
 * PWA install-prompt event capture.
 * Chrome / Edge fire `beforeinstallprompt` once per session. We capture the
 * event so a custom editorial install strip can call `.prompt()` on user tap.
 *
 * Per UX-AC-063 / AC-101: prompt is surfaced as a hairline strip with
 * dismissable state persisted in localStorage as `cn:installPromptShown`.
 */
import * as React from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const LS_DISMISSED = 'cn:installPromptDismissed';

export function useInstallPrompt() {
  const [event, setEvent] = React.useState<BeforeInstallPromptEvent | null>(
    null,
  );
  const [dismissed, setDismissed] = React.useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    try {
      return window.localStorage.getItem(LS_DISMISSED) === '1';
    } catch {
      return false;
    }
  });

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    function onBefore(e: Event) {
      e.preventDefault();
      setEvent(e as BeforeInstallPromptEvent);
    }
    window.addEventListener('beforeinstallprompt', onBefore);
    return () => window.removeEventListener('beforeinstallprompt', onBefore);
  }, []);

  function dismiss(): void {
    try {
      window.localStorage.setItem(LS_DISMISSED, '1');
    } catch {
      /* silent — private mode */
    }
    setDismissed(true);
  }

  async function install(): Promise<void> {
    if (!event) return;
    await event.prompt();
    const choice = await event.userChoice;
    if (choice.outcome === 'accepted' || choice.outcome === 'dismissed') {
      // Either way the browser handles it from here; remember and hide.
      dismiss();
    }
  }

  return {
    available: Boolean(event) && !dismissed,
    install,
    dismiss,
  };
}
